import { theme } from "antd";
import { ThemeConfig } from "antd/es/config-provider";
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type IThemeMode  = "default" | "compact";
type IThemeStyle = "light" | "dark";
type SetStateType<T> = Dispatch<SetStateAction<T>>;

interface IThemeStore {
  mode: IThemeMode;
  themeStyle: IThemeStyle;
  setThemeStyle: SetStateType<IThemeStyle>;
  themConfig: ThemeConfig;
  changeTheme: (opt: { mode?: IThemeMode; themeStyle?: IThemeStyle }) => void;
}

const ThemeStoreContext = createContext<IThemeStore | undefined>(undefined);

/* ─── shared tokens ─────────────────────────────────── */
const SHARED_TOKENS = {
  colorPrimary:  '#FBAD16',
  colorLink:     '#D9940E',
  colorLinkHover:'#FBAD16',
  fontFamily:    "'JetBrains Mono', 'Fira Code', monospace",
  borderRadius:  6,
};

const LIGHT_CONFIG: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    ...SHARED_TOKENS,
    colorBgBase:      '#FFFFFF',
    colorBgContainer: '#F8F8F8',
    colorBgElevated:  '#FFFFFF',
    colorBorder:      'rgba(0,0,0,0.10)',
    colorText:        '#0F172A',
    colorTextSecondary: '#475569',
    colorTextTertiary:  '#64748B',
  },
  components: {
    Layout:     { bodyBg: '#FFFFFF', headerBg: 'transparent' },
    Card:       { colorBgContainer: '#F8F8F8' },
    Button:     { colorPrimary: '#FBAD16', colorPrimaryHover: '#FFC03D', defaultColor: '#0F172A', defaultBorderColor: 'rgba(0,0,0,0.12)' },
    Input:      { colorBgContainer: '#FFFFFF', colorBorder: 'rgba(0,0,0,0.12)', colorText: '#0F172A', activeBorderColor: '#FBAD16', hoverBorderColor: 'rgba(251,173,22,0.5)' },
    Modal:      { contentBg: '#FFFFFF', headerBg: '#FFFFFF' },
    Pagination: { itemBg: '#F8F8F8' },
    Select:     { colorBgContainer: '#FFFFFF' },
    Drawer:     { colorBgElevated: '#FFFFFF' },
  },
};

const DARK_CONFIG: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...SHARED_TOKENS,
    colorBgBase:      '#0C0C0C',
    colorBgContainer: '#111111',
    colorBgElevated:  '#161616',
    colorBorder:      'rgba(255,255,255,0.08)',
    colorText:        '#E2E8F0',
    colorTextSecondary: '#94A3B8',
    colorTextTertiary:  '#64748B',
  },
  components: {
    Layout:     { bodyBg: '#0C0C0C', headerBg: 'transparent' },
    Card:       { colorBgContainer: '#111111' },
    Button:     { colorPrimary: '#FBAD16', colorPrimaryHover: '#FFC03D', defaultColor: '#E2E8F0', defaultBorderColor: 'rgba(255,255,255,0.12)' },
    Input:      { colorBgContainer: '#111111', colorBorder: 'rgba(255,255,255,0.12)', colorText: '#E2E8F0', activeBorderColor: '#FBAD16', hoverBorderColor: 'rgba(251,173,22,0.5)' },
    Modal:      { contentBg: '#111111', headerBg: '#111111' },
    Pagination: { itemBg: '#111111' },
    Select:     { colorBgContainer: '#111111' },
    Drawer:     { colorBgElevated: '#0C0C0C' },
  },
};

/* ─── helpers ───────────────────────────────────────── */
function applyThemeToDOM(style: IThemeStyle) {
  document.documentElement.setAttribute('data-theme', style);
  document.body.setAttribute('data-theme', style);
}

function getSavedTheme(): IThemeStyle {
  try {
    const saved = localStorage.getItem('ape-theme') as IThemeStyle | null;
    if (saved === 'dark' || saved === 'light') return saved;
  } catch (_) {}
  return 'light'; // default
}

/* ─── provider ──────────────────────────────────────── */
const ThemeStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode]             = useState<IThemeMode>('default');
  const [themeStyle, setThemeStyle] = useState<IThemeStyle>(getSavedTheme);
  const [themConfig, setThemConfig] = useState<ThemeConfig>(
    getSavedTheme() === 'dark' ? DARK_CONFIG : LIGHT_CONFIG
  );

  // Sync DOM attribute + Ant Design config whenever themeStyle changes
  useEffect(() => {
    applyThemeToDOM(themeStyle);
    setThemConfig(themeStyle === 'dark' ? DARK_CONFIG : LIGHT_CONFIG);
    try { localStorage.setItem('ape-theme', themeStyle); } catch (_) {}
  }, [themeStyle]);

  // Apply immediately on mount (before any paint)
  useEffect(() => {
    applyThemeToDOM(themeStyle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTheme = useCallback(
    (opt: { mode?: IThemeMode; themeStyle?: IThemeStyle }) => {
      if (opt.mode)       setMode(opt.mode);
      if (opt.themeStyle) setThemeStyle(opt.themeStyle);
    },
    []
  );

  const toggleTheme = useCallback(() => {
    setThemeStyle((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const values = useMemo(
    () => ({ themeStyle, setThemeStyle, mode, themConfig, changeTheme, toggleTheme }),
    [changeTheme, mode, themConfig, themeStyle, toggleTheme]
  );

  return (
    <ThemeStoreContext.Provider value={values as any}>
      {children}
    </ThemeStoreContext.Provider>
  );
};

const useThemeStore = () => {
  const context = useContext(ThemeStoreContext);
  if (!context) throw new Error('useThemeStore must be used within ThemeStoreProvider');
  return context as IThemeStore & { toggleTheme: () => void };
};

export { ThemeStoreProvider, useThemeStore };
